<template>
  <!-- "network" panel scene: one record, every party writing into it, live (the Metaship
       landing page vocabulary). A glass hub with the tenant mark, party pills on curved links,
       data pulses running into the hub, two quiet orbit rings and a soft glow. Decoration only:
       aria-hidden. Colours are tenant tokens. Motion only on .pn--motion (reduced motion: off). -->
  <svg class="pn" :class="{ 'pn--motion': animate }" viewBox="0 0 640 600" aria-hidden="true" data-test="panel-network">
    <defs>
      <radialGradient :id="ids.glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="var(--tenant-tertiary)" stop-opacity="0.32" />
        <stop offset="55%" stop-color="var(--tenant-tertiary)" stop-opacity="0.08" />
        <stop offset="100%" stop-color="var(--tenant-tertiary)" stop-opacity="0" />
      </radialGradient>
      <linearGradient :id="ids.hub" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="var(--tenant-tertiary)" stop-opacity="1" />
        <stop offset="100%" stop-color="var(--tenant-tertiary)" stop-opacity="0.62" />
      </linearGradient>
      <linearGradient :id="ids.pill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#ffffff" stop-opacity="0.11" />
        <stop offset="100%" stop-color="#ffffff" stop-opacity="0.04" />
      </linearGradient>
    </defs>

    <circle cx="320" cy="300" r="250" :fill="`url(#${ids.glow})`" />
    <g class="pn__spin"><circle class="pn__ring" cx="320" cy="300" r="238" stroke-dasharray="2 12" /></g>
    <g class="pn__spin pn__spin--rev"><circle class="pn__ring pn__ring--dash" cx="320" cy="300" r="168" stroke-dasharray="40 30" /></g>

    <path v-for="node in nodes" :key="`ln-${node.label}`" class="pn__link" :d="node.path" />
    <path
      v-for="(node, i) in nodes"
      :key="`fl-${node.label}`"
      class="pn__flow"
      pathLength="100"
      :d="node.inbound"
      :style="{ animationDelay: `${(i * 0.85).toFixed(2)}s` }"
    />

    <g v-for="(node, i) in nodes" :key="`nd-${node.label}`" :transform="`translate(${node.x} ${node.y})`">
      <!-- Solid base first: the links must stop at the pill, not show through the glass. -->
      <rect class="pn__pill-base" :x="-node.w / 2" y="-15" :width="node.w" height="30" rx="15" />
      <rect class="pn__pill" :x="-node.w / 2" y="-15" :width="node.w" height="30" rx="15" :fill="`url(#${ids.pill})`" />
      <circle class="pn__dot" :cx="-node.w / 2 + 15" cy="0" r="3.5" :style="{ animationDelay: `${(i * 0.5).toFixed(2)}s` }" />
      <text class="pn__label" :x="-node.w / 2 + 26" y="4">{{ node.label }}</text>
    </g>

    <circle class="pn__ping" cx="320" cy="300" r="46" />
    <circle class="pn__ping pn__ping--late" cx="320" cy="300" r="46" />
    <rect class="pn__hub" x="280" y="260" width="80" height="80" rx="24" :fill="`url(#${ids.hub})`" />
    <rect class="pn__hub-shine" x="281" y="261" width="78" height="38" rx="23" />
    <!-- The SAME mark as the brand tile (branding.logoLightUrl, the dark-ink cube), drawn white for the
         accent hub by a CSS filter. Was the separate white Metaship_logo.svg: an older, flatter drawing
         of the mark, so the hub looked squashed next to the tile. `meet` keeps any tenant mark whole. -->
    <image v-if="markUrl" class="pn__mark" :href="markUrl" x="298" y="278" width="44" height="44" preserveAspectRatio="xMidYMid meet" />
    <text v-else class="pn__mark-text" x="320" y="312" text-anchor="middle">{{ initial }}</text>
  </svg>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { prefersReducedMotion } from "./panelColours";

const CX = 320;
const CY = 300;
const RADIUS = 205;
/** Pill width per character of the mono label, plus padding for the dot. */
const CHAR_W = 7.4;
const PILL_PAD = 40;

/** The parties of a shipment, as the landing page names them. A tenant can name its own (up to six). */
export const DEFAULT_NETWORK_NODES = ["Exporter", "Forwarder", "Carrier", "Customs", "Buyer"];

let uid = 0;

export default defineComponent({
  name: "PanelNetwork",
  props: {
    /** Up to six party names around the hub. */
    labels: { type: Array as PropType<string[]>, default: () => [...DEFAULT_NETWORK_NODES] },
    /** The tenant mark (branding.logoLightUrl); drawn white on the accent hub tile. */
    markUrl: { type: String as PropType<string | null>, default: null },
    brand: { type: String, default: "" },
  },
  setup(props) {
    const n = ++uid;
    // Unique gradient ids: two panels on one page must not share url(#…) targets.
    const ids = { glow: `pn-glow-${n}`, hub: `pn-hub-${n}`, pill: `pn-pill-${n}` };

    const nodes = computed(() => {
      const names = (props.labels.length ? props.labels : DEFAULT_NETWORK_NODES).slice(0, 6);
      return names.map((name, i) => {
        // Evenly around the hub, starting at the top.
        const angle = -Math.PI / 2 + (i * 2 * Math.PI) / names.length;
        const x = Math.round(CX + RADIUS * Math.cos(angle));
        const y = Math.round(CY + RADIUS * Math.sin(angle) * 0.9);
        // A gentle curve: the control point sits beside the midpoint, always to the same side.
        const mx = (CX + x) / 2;
        const my = (CY + y) / 2;
        const px = -(y - CY) * 0.16;
        const py = (x - CX) * 0.16;
        const c = `${Math.round(mx + px)} ${Math.round(my + py)}`;
        const label = name.toUpperCase();
        return {
          label,
          x,
          y,
          w: Math.round(label.length * CHAR_W + PILL_PAD),
          path: `M${CX} ${CY} Q${c} ${x} ${y}`,
          inbound: `M${x} ${y} Q${c} ${CX} ${CY}`,
        };
      });
    });
    const initial = computed(() => (props.brand.trim()[0] || "M").toUpperCase());
    const animate = !prefersReducedMotion();
    return { ids, nodes, initial, animate };
  },
});
</script>

<style scoped>
.pn {
  width: 100%;
  height: auto;
  overflow: visible;
}
.pn__ring {
  fill: none;
  stroke: rgba(var(--si-ink, 255, 255, 255), 0.5);
  stroke-width: 1;
  opacity: 0.22;
}
.pn__ring--dash {
  stroke: var(--tenant-tertiary);
  opacity: 0.28;
}
.pn__link {
  fill: none;
  stroke: rgba(var(--si-ink, 255, 255, 255), 0.16);
  stroke-width: 1.2;
}
.pn__flow {
  fill: none;
  stroke: var(--tenant-tertiary);
  stroke-width: 2.6;
  stroke-linecap: round;
  /* pathLength=100: a 12-unit pulse travels the whole curve whatever its real length. */
  stroke-dasharray: 12 120;
  stroke-dashoffset: 12;
  opacity: 0;
  filter: drop-shadow(0 0 4px var(--tenant-tertiary));
}
.pn__pill-base {
  fill: var(--si-deep, #141e2c);
}
.pn__pill {
  stroke: rgba(var(--si-ink, 255, 255, 255), 0.2);
  stroke-width: 1;
}
.pn__dot {
  fill: var(--tenant-tertiary);
}
.pn__label {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 1.6px;
  fill: rgba(var(--si-ink, 255, 255, 255), 0.86);
}
.pn__ping {
  fill: none;
  stroke: var(--tenant-tertiary);
  stroke-width: 1.5;
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
}
.pn__hub {
  filter: drop-shadow(0 14px 30px rgba(0, 0, 0, 0.45));
}
.pn__hub-shine {
  fill: #fff;
  opacity: 0.12;
}
.pn__mark {
  filter: brightness(0) invert(1);
}
.pn__mark-text {
  font-size: 32px;
  font-weight: 700;
  fill: #fff;
}

/* Scoped descendant selectors only. Never `:global(x) .y`: Vue compiles it to a bare `x`,
   which animated the whole page on every tenant (2026-11-01). */
.pn--motion .pn__spin {
  transform-box: view-box;
  transform-origin: 320px 300px;
  animation: pn-spin 80s linear infinite;
}
.pn--motion .pn__spin--rev {
  animation-duration: 110s;
  animation-direction: reverse;
}
.pn--motion .pn__flow {
  animation: pn-flow 4.25s cubic-bezier(0.45, 0, 0.3, 1) infinite;
}
.pn--motion .pn__dot {
  animation: pn-twinkle 2.6s ease-in-out infinite;
}
.pn--motion .pn__ping {
  animation: pn-ping 3.6s ease-out infinite;
}
.pn--motion .pn__ping--late {
  animation-delay: 1.8s;
}

@keyframes pn-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes pn-flow {
  0% {
    stroke-dashoffset: 12;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  70% {
    opacity: 1;
  }
  80%,
  100% {
    stroke-dashoffset: -100;
    opacity: 0;
  }
}
@keyframes pn-twinkle {
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 1;
  }
}
@keyframes pn-ping {
  0% {
    transform: scale(0.6);
    opacity: 0.55;
  }
  100% {
    transform: scale(2.6);
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .pn * {
    animation: none !important;
  }
}
</style>
